import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginScreen } from "./components/loginScreen";
import { Main } from "./components/main";
import * as localForage from "localforage";
import { WelcomeScreen } from "./components/views/welcomeScreen";
import { CredentialsHandler, LF_LAST_PATH } from "./lib/lib.js";
import { useRefreshIfOld } from "./lib/hooks/useRefreshIfOld.js";
import { UserInfo } from "@ckal-software/ckal-lib/dist/common/lib.js";

export function App() {
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [isGettingUserInfo, setIsGettingUserInfo] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [isFirstLoginTry, setIsFirstLoginTry] = useState(true);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);

  const location = useLocation();
  const locationRef = useRef(location);
  const navigate = useRef(useNavigate());

  locationRef.current = location;

  useRefreshIfOld();

  const updateUserInfo = useCallback(async () => {
    setIsGettingUserInfo(true);

    try {
      const userInfo = await CredentialsHandler.getUserInfo();

      if (locationRef.current.pathname === "/") {
        const storedPath = await localForage.getItem<string>(LF_LAST_PATH);

        if (storedPath && locationRef.current.pathname === "/") {
          navigate.current(storedPath, { replace: true });
        }
      }

      setUserInfo(userInfo);
    } catch {
      return;
    } finally {
      setIsGettingUserInfo(false);
    }
  }, []);

  useEffect(() => {
    updateUserInfo();
  }, [updateUserInfo]);

  const finishInitialLoad = useCallback(() => {
    setIsLoadingInitialData(false);
  }, []);

  const showWelcome =
    isLoadingInitialData &&
    ((isGettingUserInfo && isFirstLoginTry && !isWorking) || !!userInfo);

  return (
    <div
      style={{
        height: "100vh",
        background: showWelcome ? "transparent" : "white",
        width: "100vw",
      }}
    >
      {userInfo ? (
        <Main
          userInfo={userInfo}
          onInitialLoadComplete={finishInitialLoad}
          onLogout={async () => {
            setIsFirstLoginTry(false);
            await CredentialsHandler.clearCredentials();
            setUserInfo(undefined);
            setIsLoadingInitialData(true);
          }}
        />
      ) : !showWelcome ? (
        <LoginScreen
          onLogin={() => {
            localForage.setItem(LF_LAST_PATH, "/week-plan");
            updateUserInfo();
          }}
          isWorking={isWorking}
          setIsWorking={setIsWorking}
        />
      ) : null}
      {showWelcome && (
        <div style={{ position: "fixed", inset: 0, zIndex: 12000 }}>
          <WelcomeScreen />
        </div>
      )}
    </div>
  );
}
